import type { LiveSummaryP1Shadow } from '../../api/liveSummaryApi';

export type P1IndexSampleView = { label: string; horizon: string; returnPct: number };
export type P1ShadowView = {
  available: boolean;
  stale: boolean;
  diagnosticOnly: boolean;
  title: string;
  summary: string;
  quoteStatus: string;
  stockStatus: string;
  snapshotAt: string;
  windowEnd: string;
  observationCount: number | null;
  changes: P1IndexSampleView[];
  releaseGate: string;
};

const labels: Record<string, string> = {
  KOSPI: 'KOSPI', KOSDAQ: 'KOSDAQ', KOSPI200: 'KOSPI200', KOSDAQ150: 'KOSDAQ150',
};

/** Pure, fail-closed renderer input. Unknown P1 contracts never become a signal. */
export function toP1ShadowView(shadow?: LiveSummaryP1Shadow | null, nowMs: number = Date.now()): P1ShadowView {
  const blank: P1ShadowView = {
    available: false, diagnosticOnly: true, stale: false, title: '시장 판단 검증 대기',
    summary: '이 구간에는 실시간 관측 결과가 연결되지 않았습니다.',
    quoteStatus: 'NO_DATA', stockStatus: 'NO_DATA', snapshotAt: '', windowEnd: '',
    observationCount: null, changes: [], releaseGate: 'BLOCKED',
  };
  if (!shadow || shadow.schema_version !== 'POLICYINTEL_DIGEST_BRIDGE'
    || shadow.mode !== 'SHADOW_DIAGNOSTIC_ONLY') return blank;

  const endMs = Date.parse(shadow.window_end ?? '');
  const ageMs = nowMs - endMs;
  const stale = !Number.isFinite(endMs) || ageMs > 30 * 60 * 1000 || ageMs < -2 * 60 * 1000;
  const rt = shadow.realtime_observation;
  const quoteStatus = rt?.index_status ?? 'NO_DATA';
  const stockStatus = rt?.stocks_status ?? 'NO_DATA';
  const historyCount = rt?.history_sample_count;
  const observationCount = typeof historyCount === 'number' && Number.isFinite(historyCount) && historyCount >= 0
    ? Math.trunc(historyCount) : null;
  const changes: P1IndexSampleView[] = [];
  const samples = rt?.sampled_index_changes ?? {};
  for (const horizon of ['10m', '30m', '60m']) {
    const byIndex = samples[horizon] ?? {};
    for (const key of ['KOSPI', 'KOSDAQ', 'KOSPI200', 'KOSDAQ150']) {
      const row = byIndex[key];
      // Even a numeric value is suppressed unless the independent sample validation passed.
      if (row?.status !== 'OBSERVED' || typeof row.return_pct !== 'number'
        || !Number.isFinite(row.return_pct)) continue;
      changes.push({ label: labels[key], horizon, returnPct: row.return_pct });
    }
  }
  const summary = quoteStatus === 'NO_FRESH_QUOTES'
    ? '국내 정규장 이후 최신 유효 시세가 없어 방향을 판단하지 않습니다.'
    : quoteStatus === 'OBSERVED'
      ? '기존 KIS 지수 관측을 확인했습니다. 시장 방향·뉴스 인과는 별도 검증이 필요합니다.'
      : '지수 관측값이 부족하거나 확인되지 않아 시장 방향을 보류합니다.';
  return {
    ...blank,
    available: true,
    stale,
    title: stale ? '이전 10분 구간 · 검증 대기' : '시장 판단 검증 대기',
    summary: stale ? `이전 구간 진단입니다. ${summary}` : summary,
    quoteStatus,
    stockStatus,
    snapshotAt: rt?.captured_at ?? '',
    windowEnd: shadow.window_end ?? '',
    observationCount,
    changes: changes.slice(0, 8),
    releaseGate: shadow.release_gate ?? 'BLOCKED',
  };
}
